<template>
  <div class="q-pa-md" v-if="enableFilters" ref="filters">
    <div class="row q-col-gutter-sm">
      <table-filters-menu
        :options="tableData.meta.columns"
        :selections="tableData.meta.defaults"
        :selected-sorted-value="sorting"
        :ascending-value="getAscendingValue"
        @toggle-sort="toggleAscending"
        @grouping-change="onGroupingChange"
        @sorting-change="onSortingChange"
        @filter-change="onFilterChange"
        @toggle-conditions="onToggleConditions"
      ></table-filters-menu>
      <div class="row full-width" v-show="showConditions">
        <filters-stackable
          ref="filtersStackableRef"
          class="full-width"
          :fields="tableData.meta.columns"
          :default-conditions="tableData.meta.defaultConditions"
          @condition-stack="setConditionStack"
          @close-condition-stack="onToggleConditions"
        >
        </filters-stackable>
      </div>
      <div class="row full-width">
        <slot name="actions" :checked-data="checkedData"> </slot>
      </div>
    </div>
  </div>
  <template v-if="!isLoading">
    <template v-if="filteredRows.length">
      <template v-if="displayType == 'table'">
        <div
          v-bind="containerProps"
          class="q-markup-table q-table__container q-table__card q-table--horizontal-separator q-table--dense q-table--flat q-table--bordered q-table--no-wrap my-sticky-header-table sticky-top"
          style="max-height: 60vh"
        >
          <table
            v-if="displayType === 'table'"
            v-bind="wrapperProps"
            class="my-sticky-header-table sticky-top q-table q-table--dense q-table--flat q-table--bordered q-table--no-wrap"
            style="overflow-y: auto; max-height: 60vh"
            @checked-change="onCheckedDataChange"
          >
            <template v-if="groupedDataHeadings.length > 0">
              <template v-for="(group, index) in groupedDataHeadings" :key="JSON.stringify(group)">
                <BaseTable
                  ref="baseTableRef"
                  check-header
                  :columns="tableData.meta.columns"
                  :sorted-column="sorting"
                  :sorted-value="getAscendingValue"
                  @toggle-sort="toggleAscending"
                  @set-sort="onSortingChange"
                  @checked-change="onCheckedDataChangeGrouped($event, index)"
                  @collapsed="setCollapse($event)"
                  :filter="filter"
                  :collapsed="collapsedMap[group]"
                  :grouped-data-headings="groupedDataHeadings"
                  :group-heading="group ? group.toString() : undefined"
                  :rows="listFilter(group)"
                  :all-rows="groupedRows(group)"
                  :rows-length="groupedRows(group).length"
                  :checkbox="options?.checks"
                  :route="options?.route"
                  :cell-options="options?.cellOptions"
                  :table-ref="tableRef"
                >
                  <template
                    v-for="(slot, slotIndex) of Object.keys($slots)"
                    :key="slotIndex"
                    #[slot]="props"
                  >
                    <slot :name="slot" v-bind="props"></slot>
                  </template>
                </BaseTable>
              </template>
            </template>
            <template v-else>
              <BaseTable
                use-list-index
                ref="baseTableRef"
                :columns="tableData.meta.columns"
                :sorted-column="sorting"
                :sorted-value="getAscendingValue"
                @toggle-sort="toggleAscending"
                @set-sort="onSortingChange"
                @checked-change="onCheckedDataChange"
                @collapsed="setCollapse($event)"
                :filter="filter"
                :collapsed="collapse"
                :group-heading="t('data.allData')"
                :rows="list"
                :all-rows="filteredRows"
                :rows-length="filteredRows.length"
                :checkbox="options?.checks"
                :route="options?.route"
                :cell-options="options?.cellOptions"
                :table-ref="tableRef"
              >
                <template
                  v-for="(slot, slotIndex) of Object.keys($slots)"
                  :key="slotIndex"
                  #[slot]="props"
                >
                  <slot :name="slot" v-bind="props"></slot>
                </template>
              </BaseTable>
            </template>
          </table>
        </div>
      </template>
      <div v-else class="card-view rounded-borders">
        <template v-if="groupedDataHeadings.length > 0">
          <data-card
            v-for="group in groupedDataHeadings"
            :key="group"
            :columns="tableData.meta.columns"
            :rows="groupedRows(group)"
            :group-heading="group.toString()"
            :collapsed="collapse"
            :filter="filter"
          ></data-card>
        </template>
        <data-card
          v-else
          :columns="tableData.meta.columns"
          :rows="filteredRows"
          :collapsed="collapsed"
          :filter="filter"
        ></data-card>
      </div>
    </template>
    <template v-else>
      <div>
        {{ t('data.noData') }}
      </div>
    </template>
  </template>
  <template v-if="isLoading">
    <div>
      <q-inner-loading :showing="isLoading">
        <q-spinner label="Please wait..." size="50px" color="primary" />
      </q-inner-loading>
    </div>
  </template>
</template>

<script lang="ts">
import BaseTable from 'components/BaseTable.vue'
import FiltersStackable from 'components/FiltersStackable.vue'
import TableFiltersMenu from 'components/TableFiltersMenu.vue'
import { defineComponent, computed, ref, Ref, PropType, onBeforeMount } from 'vue'
import { useGroup } from '../composables/group'
import { useFilter } from '../composables/filter'
import { SortingKeyType, useSortAlgorithm } from '../composables/sortAlgorithm'
import {
  Conditions,
  getDateModifier,
  getOperators,
  useConditionals,
} from '../composables/conditionals'
import { useI18n } from 'vue-i18n'
import { DisplayType, FilteringDisplayOptions } from '../types/components/data-rendering'
import DataCard from './DataCard.vue'
import { NewCondition } from '../store/helpers/useSetDefaultConditions'
import { useVirtualList } from '@vueuse/core'
import { Capacitor } from '@capacitor/core'

export default defineComponent({
  name: 'FilteringDataDisplay',
  components: { BaseTable, TableFiltersMenu, FiltersStackable, DataCard },
  props: {
    isLoading: {
      required: false,
      type: Boolean,
      default: false,
    },
    tableData: {
      required: true,
      type: Object,
      default: () => ({ data: [], meta: {} }),
    },
    enableFilters: {
      required: false,
      type: Boolean,
      default: true,
    },
    displayType: {
      required: false,
      type: String as PropType<DisplayType>,
      default: 'table',
    },
    options: {
      requried: false,
      type: Object as PropType<FilteringDisplayOptions>,
      default: undefined,
    },
    collapse: {
      required: false,
      type: Boolean,
      default: false,
    },
  },
  setup(props, { expose }) {
    const { t } = useI18n({ useScope: 'global' })

    //Filtering
    const filter: Ref<string | null> = ref(null)
    const { filterData } = useFilter()
    //Sorting
    const sorting: Ref<string | null> = ref(null)
    const { sortTable, setSortingParams, toggleAscending, resetAscending, getAscendingValue } =
      useSortAlgorithm()
    const sortingKeyType: Ref<SortingKeyType> = ref(null)
    const setSortingKeyType = (value: SortingKeyType) => {
      return (sortingKeyType.value =
        value !== null ? props.tableData.meta.columns[value].type : null)
    }
    setSortingParams(sorting.value, sortingKeyType.value)

    //set default collapsed map
    const setCollapseMap = (collapseAll = false) => {
      for (const group of groupedDataHeadings.value) {
        if (props.collapse || collapseAll) {
          setCollapse({ group, collapsed: true }, false)
        } else {
          setCollapse({ group, collapsed: false }, false)
        }
      }
    }

    onBeforeMount(() => {
      if (props.tableData.meta.defaults != undefined) {
        if (props.tableData.meta.defaults.sortBy != undefined) {
          setSortingKeyType(props.tableData.meta.defaults.sortBy)
          setSortingParams(sorting.value, sortingKeyType.value)
        }
        if (props.tableData.meta.defaults.groupBy != undefined) {
          grouping.value = props.tableData.meta.defaults.groupBy
        }
      }
      // if (props.collapse) {
      setCollapseMap()
      // }
    })
    // watch(
    //   () => props.tableData,
    //   (currentValue) => {
    //     if (currentValue.meta.defaults != undefined) {
    //       if (currentValue.meta.defaults.sortBy != undefined) {
    //         setSortingKeyType(currentValue.meta.defaults.sortBy)
    //         setSortingParams(sorting.value, sortingKeyType.value)
    //       }
    //       if (currentValue.meta.defaults.groupBy != undefined) {
    //         grouping.value = currentValue.meta.defaults.groupBy
    //       }
    //     }
    //     // if (props.collapse) {
    //     //   setCollapseMap()
    //     // }
    //   }
    // )

    //Filter stacking
    const filtersStackableRef = ref()
    const globalConditionStack: Ref<Conditions> = ref([])
    const addCondition = (condition: NewCondition) => {
      // globalConditionStack.value.push(condition)
      filtersStackableRef.value.addCondition({
        field: { name: condition.col, ...props.tableData.meta.columns[condition.col] },
        operation: getOperators(condition.operation),
        dateModifier: condition.dateModifier ? getDateModifier(condition.dateModifier) : undefined,
        value: condition.value,
        options: condition.options ? condition.options : undefined,
        optionsKey: condition.optionsKey ? condition.optionsKey : undefined,
      })
    }
    // const { conditionStack, conditionalFilterInput, returnFilteredData } = useConditionals()
    // conditionalFilterInput.value = props.tableData.data
    const setConditionStack = (e: Conditions) => {
      globalConditionStack.value = e
    }
    const filteredRows = computed(() => {
      const { conditionStack, conditionalFilterInput, returnFilteredData } = useConditionals()
      conditionStack.value = globalConditionStack.value
      conditionalFilterInput.value = filterData(props.tableData.data, filter.value)
      return sortTable(returnFilteredData.value)
    })
    //Grouping
    const grouping: Ref<string | null> = ref(null)
    const { groupDataHashMap, groupedDataHeadings } = useGroup(filteredRows, grouping)
    const groupedRows = (groupBy?: any) => {
      return groupDataHashMap.value[groupBy]
    }

    const baseTableRef = ref()
    //Events
    const onGroupingChange = (e: any) => {
      grouping.value = e
      const shouldCollapse = !!e
      setCollapseMap(shouldCollapse)
    }
    const onSortingChange = (e: any) => {
      sorting.value = e
      resetAscending()
      setSortingKeyType(e)
      setSortingParams(sorting.value, sortingKeyType.value)
    }
    const onFilterChange = (e: any) => {
      filter.value = e
    }
    const showConditions: Ref<boolean> = ref(true)
    const onToggleConditions = () => {
      showConditions.value = !showConditions.value
    }
    const checkedData = ref()
    const onCheckedDataChange = (e: any) => {
      checkedData.value = e
    }
    const checkedDataGroup = ref<any[]>([])
    const onCheckedDataChangeGrouped = (e: any, index: number) => {
      checkedDataGroup.value[index] = e
      checkedData.value = checkedDataGroup.value.flat()
    }

    const unCheckAll = () => {
      if (baseTableRef.value) {
        if (baseTableRef.value.length) {
          baseTableRef.value.forEach((table: any) => table.unCheckAll())
        } else {
          baseTableRef.value?.unCheckAll()
        }
      }
    }

    expose({ addCondition, unCheckAll })
    const tableRef = ref(null)
    //Keep track of what has been collapsed
    const collapsedMap = ref<{ [key: string]: boolean }>({})
    const setCollapse = (e: any, scroll = true) => {
      collapsedMap.value[e.group] = e.collapsed
      if (scroll) {
        const groupIndex = groupedDataHeadings.value.findIndex((heading) => heading == e.group)
        if (e.collapsed) {
          // we just collapsed a group, so scroll to the next one (if it exists)
          const nextGroupIndex = groupIndex + 1
          const nextGroup = groupedDataHeadings.value[nextGroupIndex]
          if (nextGroup) {
            // A next group exists, scroll to it
            const scrollToIndex = vListRows.value.findIndex((row) => row._group == nextGroup)
            scrollTo(scrollToIndex)
          } else {
            // There is no next group, scroll to the end of the list
            scrollTo(vListRows.value.length - 1)
          }
        } else {
          // scroll the list to the first item in the group we just opened
          const currentGroup = groupedDataHeadings.value[groupIndex]
          if (currentGroup) {
            const firstIndex = vListRows.value.findIndex((row) => row._group == currentGroup)
            scrollTo(firstIndex)
          }
        }
      }
    }

    const vListRows = computed(() => {
      if (groupedDataHeadings.value.length > 0) {
        const returnData = []
        for (const group of groupedDataHeadings.value) {
          if (!collapsedMap.value[group]) {
            const groupedData = groupedRows(group)
            //make first item taller since it needs to inc the col headers and group header
            groupedData[0]._itemHeight = GROUP_HEADER_HEIGHT + COL_HEADER_HEIGHT + ROW_HEIGHT
            returnData.push(...groupedData)
          } else {
            //if collapsed we need to push some breadcrumb header
            returnData.push({
              _group: group,
              _breadcrumb: true,
              _itemHeight: GROUP_HEADER_HEIGHT,
              _index: 0,
            })
          }
        }
        return returnData
      } else {
        if (!collapsedMap.value[t('data.allData')]) {
          // const returnData = []
          // for (let index = 0; index < filteredRows.value.length; index++) {
          //   const row = filteredRows.value[index]
          //   returnData.push({ _index: index, ...row })
          // }
          return filteredRows.value
        } else {
          return []
        }
      }
    })

    const GROUP_HEADER_HEIGHT = 40
    const COL_HEADER_HEIGHT = 28
    const ROW_HEIGHT = Capacitor.isNativePlatform() ? 28 : 28.5
    const { list, containerProps, wrapperProps, scrollTo } = useVirtualList(vListRows, {
      itemHeight: (index) => {
        if (vListRows.value[index]._itemHeight) {
          return vListRows.value[index]._itemHeight
        }
        return ROW_HEIGHT
      },
      overscan: 20,
    })
    const listFilter = (group: string) =>
      list.value.filter((row: any) => row.data._group == String(group))

    return {
      onGroupingChange,
      onSortingChange,
      onFilterChange,
      onToggleConditions,
      checkedData,
      onCheckedDataChange,
      onCheckedDataChangeGrouped,
      showConditions,
      filter,
      filteredRows,
      groupedRows,
      groupedDataHeadings,
      grouping,
      sorting,
      toggleAscending,
      getAscendingValue,
      setConditionStack,
      t,
      globalConditionStack,
      filtersStackableRef,
      baseTableRef,
      tableRef,
      list,
      listFilter,
      containerProps,
      wrapperProps,
      vListRows,
      collapsedMap,
      setCollapse,
    }
  },
})
</script>
<style>
.sticky-top {
  position: sticky;
  top: 0;
}
.sticky-filters {
  position: sticky;
  top: 78px; /* equal to the header offset + the Stores.vue padding */
}
.my-sticky-header-table {
  /* height or max-height is important */
  /* max-height: 60vh; */
  /* height: 60vh; */
  max-height: 400vh;
  overflow-y: hidden;
  /* this is when the loading indicator appears */
}
.my-sticky-header-table .q-table__top,
.my-sticky-header-table .q-table__bottom,
.my-sticky-header-table thead tr:first-child th {
  /* bg color is important for th; just specify one */
  background-color: #c1f4cd;
}
.my-sticky-header-table thead tr th {
  position: sticky;
  z-index: 1;
}
.my-sticky-header-table thead tr:first-child th {
  top: 0;
  left: 0;
}
.my-sticky-header-table.q-table--loading thead tr:last-child th {
  /* height of all previous header rows */
  top: 48px;
}
.card-view {
  border: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
